<template>
  <div>
    <div
      :id="mapId"
      class="mapContainer"
    />
    <div
      id="modalSiteDetails"
      style="display:none"
    >
      <div class="row pb-1">
        <div class="col-12">
          {{ locationAddress }}
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <h1 class="h5">
            {{ coverageGroup }} - Coverage:
          </h1>
        </div>
      </div>
      <coverage-info-grid-row
        v-for="(item, index) in coverageInfoData"
        :key="`val${index}`"
        :callback-url="item.callbackUrl"
        :attribute-detail="item.attributeDetail"
        :coordinate="item.coordinate"
        :link-target="linkTarget"
      />
      <div class="row pt-2">
        <div class="col-12">
          <a
            class="btn btn-primary btn-block"
            :href="actionUrl"
            :target="linkTarget"
          >{{ actionDescription }}</a>
        </div>
      </div>
      <div class="row text-center">
        <div
          v-if="isFaultGroup"
          class="col-12"
        >
          <button
            @click="showModal"
            type="button"
            class="btn btn-link"
          >
            Log a coverage fault
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { clearTimeout, setTimeout } from 'timers';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet/dist/leaflet';
import coordinate from '@/components/validators/coordinate';
import CoverageInfoGridRow from '@/components/CoverageInfo/GridRow.vue';
import EventBus from '@/eventBus';

const markerURL = require('@/assets/pins/TW_VodaPinsArtboard 20.png');

export default {
  components: {
    CoverageInfoGridRow,
  },
  computed: {
    isFaultGroup() {
      return !this.faultGroupsData
        ? false
        : this.faultGroupsData.some((v) => v === this.coverageGroup);
    },
  },
  data() {
    return {
      actionDescription: null,
      actionUrl: null,
      baseMaps: [{ type: 'Hybrid' }],
      callingSystemData: this.callingSystem,
      coverageCoordinate: null,
      coverageGroup: this.group,
      coverageInfoData: [],
      coverageLayer: null,
      faultGroupsData: null,
      layerGroup: L.LayerGroup,
      locationAddress: null,
      map: null,
      mapHeight: `${document.documentElement.clientHeight - 80}px`,
      mapId: 'ag-map',
      mapLayerGroup: null,
      mapMarker: null,
      mapTypes: {
        hybrid: L.LayerGroup,
        vector: L.TileLayer,
        imagery: L.TileLayer,
      },
      popupTimeout: null,
      productData: this.product,
      reverseGeocodeRequest: null,
      wmsLayers: [],
    };
  },
  destroyed() {
    EventBus.$off('change-map-type');
    EventBus.$off('coordinateSet');
    EventBus.$off('coverageGroupSelected');
    EventBus.$off('layer-changed');
  },
  mounted() {
    this.initMap();
    EventBus.$on('change-map-type', this.onMapTypeChanged);
    EventBus.$on('coordinateSet', this.onCoordinateSet);
    EventBus.$on('coverageGroupSelected', this.onGroupChanged);
    EventBus.$on('layer-changed', this.onLayerChanged);
  },
  methods: {
    getCoordinateDetail() {
      if (this.popupTimeout) {
        clearTimeout(this.popupTimeout);
        this.popupTimeout = null;
      }

      this.resetPopup();
      this.initializeCoordinateOnMap();

      if (!this.showCoverageInfo) {
        return;
      }

      let coverageUrl = `${
        process.env.VUE_APP_ROOT_API
      }/api/reversegeocoding?latitude=${
        this.coverageCoordinate.latitude
      }&longitude=${this.coverageCoordinate.longitude}&coverageGroup=${
        this.coverageGroup
      }`;

      if (this.callingSystemData) {
        coverageUrl += `&callingSystem=${this.callingSystemData}`;
      }

      if (this.productData) {
        coverageUrl += `&product=${this.productData}`;
      }

      this.$http
        .get(
          coverageUrl,
          {
            before(request) {
            // abort previous request, if exists
              if (this.reverseGeocodeRequest) {
                this.reverseGeocodeRequest.abort();
              }
              // set previous request on Vue instance
              this.reverseGeocodeRequest = request;
            },
          },
        )
        .then((result) => {
          this.coverageInfoData = (!result.body.result.layerCoverages)
            ? []
            : result.body.result.layerCoverages
              .sort((a, b) => a.priority - b.priority)
              .map((r) => ({
                attributeDetail: {
                  covered: r.hasCoverage,
                  description: r.name,
                },
                callbackUrl: r.orderUrl,
                coordinate: {
                  latitude: result.body.result.latitude,
                  longitude: result.body.result.longitude,
                },
              }));
          this.actionDescription = result.body.result.actionDescription;
          this.actionUrl = result.body.result.actionUrl;
          this.locationAddress = result.body.result.description;

          this.populatePopup();
        },
        () => {
          // console.errror(error);
        });
    },
    initializeCoordinateOnMap() {
      const icon = L.icon({
        iconUrl: markerURL,
        iconSize: [50, 50],
        iconAnchor: [25, 50],
      });
      const coordArray = [
        this.coverageCoordinate.latitude,
        this.coverageCoordinate.longitude,
      ];
      const northWestViewportArray = (!this.coverageCoordinate.viewport)
        ? coordArray
        : [
          this.coverageCoordinate.viewport[0].latitude,
          this.coverageCoordinate.viewport[0].longitude,
        ];
      const southWestViewportArray = (!this.coverageCoordinate.viewport)
        ? coordArray
        : [
          this.coverageCoordinate.viewport[1].latitude,
          this.coverageCoordinate.viewport[1].longitude,
        ];

      if (this.mapMarker) {
        this.map.removeLayer(this.mapMarker);
      }
      this.mapMarker = L.marker(coordArray, {
        icon,
        draggable: true,
      });
      this.mapMarker.addTo(this.map);
      this.map.setView(coordArray);
      this.map.fitBounds([northWestViewportArray, southWestViewportArray]);

      this.mapMarker.on('click', (e) => e.preventDefault());

      this.mapMarker.on('dragend', (e) => {
        /* eslint no-underscore-dangle: ["error", { "allow": ["_latlng"] }] */
        this.coverageCoordinate.latitude = e.target._latlng.lat;
        this.coverageCoordinate.longitude = e.target._latlng.lng;
        this.coverageCoordinate.viewport = null;
        this.getCoordinateDetail();
        const msg = {
          event: 'coordinateChanged',
          payload: {
            latitude: e.target._latlng.lat,
            longitude: e.target._latlng.lng,
          },
        };
        window.parent.postMessage(msg, '*');
      });

      if (!this.showCoverageInfo) {
        return;
      }

      this.mapMarker.off('popupclose');

      this.mapMarker.on('popupclose', () => {
        this.resetPopup();
      });

      const popup = L.popup(
        {
          autoPanPaddingTopLeft: L.point(300, 350),
          closeButton: false,
          offset: [1, -20],
          minWidth: 300,
          maxWidth: 300,
          className: 'bigPopup',
          closeOnClick: false,
          closeOnEscapeKey: false,
        },
        this.mapMarker,
      );

      popup.setContent(
        '<div id="popupContainer"><div id="popupLoader" class="spinner-border" ></div></div>',
      );

      this.mapMarker.bindPopup(popup).openPopup();
    },
    initMap() {
      let initCoord = {
        latitude: -29.399111,
        longitude: 24.720288,
      };

      if (this.coordinate) {
        initCoord = this.coordinate;
      }

      this.map = window.AfriGIS.map(this.mapId, {
        authkey: process.env.VUE_APP_AG_JS_API_KEY,
        center: L.latLng(initCoord.latitude, initCoord.longitude),
        crs: L.CRS.EPSG4326,
        mapType: 'vector',
        maxBounds: L.latLngBounds(L.latLng(-90, -180), L.latLng(90, 180)),
        maxBoundViscosity: 0,
        maxZoom: 18,
        minZoom: 2,
        zoom: this.zoomLevel,
        zoomControl: true,
        zoomOffset: 1,
      });
      this.map.zoomControl.setPosition('bottomright');
    },
    loadCoverageLayer() {
      const wmsLayer = this.wmsLayers.find(
        (wms) => wms.options.layers === this.coverageLayer,
      );

      if (wmsLayer) {
        return wmsLayer;
      }
      const newWmsLayer = L.tileLayer.wms(
        `${process.env.VUE_APP_OGC_BASE_URL}`,
        {
          layers: this.coverageLayer,
          format: 'image/png',
          transparent: true,
          authkey: `${process.env.VUE_APP_OGC_AUTH_KEY}`,
          tiled: true,
        },
      );
      this.wmsLayers.push(newWmsLayer);
      return newWmsLayer;
    },
    moveContentBetweenContainers(oldContainerSelector, newContainerSelector) {
      const newParent = document.querySelector(newContainerSelector);
      const oldParent = document.querySelector(oldContainerSelector);

      while (oldParent.childNodes.length) {
        newParent.appendChild(oldParent.firstChild);
      }
    },
    onCoordinateSet(val) {
      this.coverageCoordinate = val;
      this.getCoordinateDetail();
    },
    onGroupChanged(group) {
      this.coverageGroup = group;
      if (this.coverageCoordinate) {
        this.getCoordinateDetail();
      }
    },
    onLayerChanged(layer) {
      this.coverageLayer = layer;
      this.toggleCoverageLayers();
    },
    onMapTypeChanged(mapType) {
      this.map.switchTo(mapType);
      this.toggleCoverageLayers();
    },
    populatePopup() {
      this.popupTimeout = setTimeout(() => {
        const loader = document.querySelector('#popupLoader');
        if (loader) {
          loader.parentNode.removeChild(loader);
        }

        this.moveContentBetweenContainers(
          '#modalSiteDetails',
          '#popupContainer',
        );
      }, 1000);
    },
    resetPopup() {
      if (document.querySelector('#modalSiteDetails').hasChildNodes()) {
        return;
      }
      this.moveContentBetweenContainers('#popupContainer', '#modalSiteDetails');
    },
    toggleCoverageLayers() {
      if (!this.coverageLayer) {
        return;
      }
      const layerObject = this.loadCoverageLayer();
      if (this.mapLayerGroup == null) {
        this.mapLayerGroup = L.layerGroup();
        this.mapLayerGroup.addTo(this.map);
      }
      this.mapLayerGroup.clearLayers();
      this.mapLayerGroup.addLayer(layerObject);
    },
    showModal() {
      EventBus.$emit('show-modal', this.coverageCoordinate);
    },
  },
  name: 'LeafletMap',
  props: {
    callingSystem: {
      default: null,
      required: false,
      type: String,
    },
    coordinate: {
      default: null,
      required: false,
      type: Object,
      validator: coordinate,
    },
    faultGroups: {
      default: null,
      required: false,
      type: Array,
    },
    group: {
      default: null,
      required: false,
      type: String,
    },
    linkTarget: {
      default: '_blank',
      required: false,
      type: String,
    },
    product: {
      default: null,
      required: false,
      type: String,
    },
    showCoverageInfo: {
      default: true,
      required: false,
      type: Boolean,
    },
    zoomLevel: {
      default: 5,
      required: false,
      type: Number,
      validator(value) {
        return value >= 1 && value <= 10;
      },
    },
  },
  watch: {
    callingSystem(newV) {
      this.callingSystemData = newV;
    },
    coordinate(newV) {
      this.coverageCoordinate = newV;
      if (this.group) {
        this.getCoordinateDetail();
      }
    },
    faultGroups(newV) {
      this.faultGroupsData = newV;
    },
    group(newV) {
      this.coverageGroup = newV;
      if (this.coordinate) {
        this.getCoordinateDetail();
      }
    },
    product(newV) {
      this.productData = newV;
    },
  },
};
</script>

<style scoped>
.mapContainer {
  height: 100vh;
  width: 100%;
  z-index: 1;
  position: absolute;
  top: 0;
  bottom: 0;
}

.bigPopup {
  -webkit-border-radius: 0 !important;
  -moz-border-radius: 0 !important;
  border-radius: 0 !important;
}
a.btn-primary{
  color: #fff;
}

.leaflet-container {
  font-family: Vodafone;
}
</style>
